import React, { useEffect, useLayoutEffect, useState } from "react";
import { Grid, Tab, Tabs } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { managerAction } from "redux/slice/manager";
import { getFilterDates, checkPermission } from "utils/HelperFunctions";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MultiSelectAutocomplete from "components/MultiSelectAutocomplete";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import { PERMISSIONS_NAME, USER_CONFIGS } from "lib/constants";
import { postAPICall } from "lib/callApi";
import { loginUserAction } from "redux/slice/users";
import { teamsAction } from "redux/slice/teams";
import UserView from "./UserView";
import TeamView from "./TeamView";
import WidgetGrid from "../../Widgets/WidgetGrid";

function ManagerRoleView() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.loginUser.loginUserState);
  const teamList = useSelector((state) => state.teams.allTeams);

  const [selectedUser, setSelectedUser] = useState(
    user.type === "user" &&
      user.teamId &&
      !checkPermission(PERMISSIONS_NAME?.INPUT_KPIS, user?.userPermission)
      ? 0
      : user?.id
  );
  const [selectedTeam, setSelectedTeam] = useState(null); // store selected team data which was used in team user view component
  const [selectedTab, setSelectedTab] = useState(
    user.type === "user" &&
      user.teamId &&
      !checkPermission(PERMISSIONS_NAME?.INPUT_KPIS, user?.userPermission)
      ? 1
      : 0
  );
  const [teamListOptions, setTeamListOptions] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 1) {
      setSelectedUser(0);
    } else {
      setSelectedUser(user?.id);
    }
  };

  const handleUserClick = (id) => {
    setSelectedUser(id);
  };

  useEffect(() => {
    let intervalId;

    if (user.type === "user" && selectedTab === 1) {
      const dateRange = getFilterDates("Today", user.timezone);
      const teams = selectedTeams.map((team) => team?.id);
      dispatch(managerAction.getManagerPerformanceSaga({ ...dateRange, teams }));

      intervalId = setInterval(
        () => dispatch(managerAction.getManagerPerformanceSaga({ ...dateRange, teams })),
        300000
      );
    } else if (!selectedTab && selectedTeam) {
      setSelectedTeam(null);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedTab, selectedTeams]);

  useEffect(() => {
    if (selectedTeam) setSelectedTeam(null);
    if (selectedUser) {
      if (selectedTab === 1) {
        setSelectedUser(0);
      } else {
        setSelectedUser(
          user.type === "user" &&
            user.teamId &&
            !checkPermission(PERMISSIONS_NAME?.INPUT_KPIS, user?.userPermission)
            ? 0
            : user?.id
        );
      }
    }
  }, [selectedTeams]);

  const selectedTeamsChange = (options) => setSelectedTeams(options);

  const fetchTeams = () => {
    dispatch(teamsAction.getTeamsSaga());
  };

  const saveAsDefaultFilter = async (selectedOptions) => {
    try {
      const response = await postAPICall(`${USER_CONFIGS}/${user.userCompanyLinkId}`, true, {
        value: { selectedTeams: selectedOptions },
        name: "DEFAULT_TEAM_VIEW_FILTER",
      });
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Saved",
            text: response?.data?.message || response.message || "Performance filter saved!",
          }),
          {
            variant: "custom",
          }
        );
        dispatch(
          loginUserAction.getLoginUserData({
            ...user,
            defaultTeamViewFilter: JSON.stringify({ selectedTeams: selectedOptions }),
          })
        );
        selectedTeamsChange(selectedOptions);
      } else if (response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || response.message || "Could not save data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not update data!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useLayoutEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    const newOptions = teamList?.length
      ? teamList?.map((team) => ({ id: team.id, label: team?.name }))
      : [];
    setTeamListOptions(newOptions);

    if (user?.defaultTeamViewFilter) {
      setSelectedTeams(JSON.parse(user.defaultTeamViewFilter)?.selectedTeams || newOptions);
    } else {
      setSelectedTeams(newOptions);
    }
  }, [teamList, user?.defaultTeamViewFilter]);

  return (
    <>
      <Grid container mb={2} alignItems="center">
        {checkPermission(PERMISSIONS_NAME?.INPUT_KPIS, user?.userPermission) ? (
          <Grid item width={120}>
            <MDBox sx={{ alignSelf: "center", justifySelf: "center" }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                  borderRadius: "none",
                }}
              >
                <Tab
                  icon={
                    <PersonIcon
                      sx={{
                        margin: "0 .6rem",
                      }}
                      fontSize="medium"
                    />
                  }
                />
                <Tab icon={<GroupsIcon sx={{ margin: "0 .6rem" }} fontSize="medium" />} />
              </Tabs>
            </MDBox>
          </Grid>
        ) : null}
        {selectedTab === 1 ? (
          <Grid item ml={2} xs={7} sm={8} lg={6} xl={4}>
            <MultiSelectAutocomplete
              value={selectedTeams}
              options={[
                ...teamListOptions,
                {
                  id: "customRender",
                  label: "customRender",
                  type: "customRender",
                  render: (selectedOptions) => (
                    <MDButton
                      variant="outlined"
                      color="gray80"
                      sx={{ mt: 2, ml: 2, position: "relative" }}
                      onClick={() => saveAsDefaultFilter(selectedOptions)}
                    >
                      Save as default view
                    </MDButton>
                  ),
                },
              ]}
              label="Select Teams"
              onChange={selectedTeamsChange}
            />
          </Grid>
        ) : null}
      </Grid>
      <Grid container>
        <Grid item xs={12} lg={8}>
          {selectedTab === 0 && <UserView selectedTab={selectedTab} selectedUser={selectedUser} />}
          {selectedTab === 1 && (
            <TeamView
              selectedTab={selectedTab}
              selectedUser={selectedUser}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              selectedTeams={selectedTeams}
              handleUserClick={handleUserClick}
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            [theme.breakpoints.up("lg")]: {
              mt: "-16px",
            },
          }}
        >
          <WidgetGrid
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
            selectedUser={selectedUser}
            selectedTeam={selectedTeam}
            selectedTeams={selectedTeams}
            handleUserClick={handleUserClick}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default ManagerRoleView;
